/* eslint-disable no-console */
import Axios, { AxiosInstance } from 'axios';

import https from 'https';
import { IApiClient } from './interfaces';
import { IApiResponse } from './interfaces/api-client.interface';

export default class ApiClient implements IApiClient {
  private client: AxiosInstance;

  // eslint-disable-next-line class-methods-use-this
  protected createAxiosClient(baseUrl: string): AxiosInstance {
    const axios = Axios.create({
      baseURL: baseUrl,
      responseType: 'json',
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 10 * 1000,
      ...(process.env.NODE_ENV === 'development' && {
        httpsAgent: new https.Agent({
          rejectUnauthorized: false,
        }),
      }),
    });

    return axios;
  }

  constructor(baseUrl: string) {
    this.client = this.createAxiosClient(baseUrl);
  }

  async get<TResponse>(path: string): Promise<TResponse> {
    try {
      const response = await this.client.get<TResponse>(path);
      return response.data;
    } catch (error) {
      if (Axios.isAxiosError(error)) {
        if (error.response?.status && error.response.status > 499) {
          console.error(error.response.data);
        }
      } else {
        console.error(error);
      }
    }
    return {} as TResponse;
  }

  async postWithResponse<TResponse>(
    path: string,
    data?: object
  ): Promise<TResponse> {
    try {
      const response = await this.client.post(path, data);
      return response.data;
    } catch (error) {
      if (Axios.isAxiosError(error)) {
        if (error.response?.data) {
          console.error(error.response.data);
        }
      } else {
        console.error(error);
      }
      return {} as TResponse;
    }
  }

  async post(path: string, data?: object): Promise<IApiResponse> {
    try {
      const response = await this.client.post(path, data);
      return { success: response.status < 299, message: response.data };
    } catch (error) {
      if (Axios.isAxiosError(error)) {
        if (error.response?.data) {
          console.error(error.response.data);
        }
      } else {
        console.error(error);
      }
      return { success: false };
    }
  }

  async delete(path: string, data?: object): Promise<IApiResponse> {
    try {
      const response = await this.client.delete(path, data);
      return { success: response.status < 299, message: response.data };
    } catch (error) {
      if (Axios.isAxiosError(error)) {
        if (error.response?.data) {
          console.error(error.response.data);
        }
      } else {
        console.error(error);
      }
      return { success: false };
    }
  }

  async patch(path: string, data?: object): Promise<IApiResponse> {
    try {
      const response = await this.client.patch(path, data);
      return { success: response.status < 299, message: response.data };
    } catch (error) {
      if (Axios.isAxiosError(error)) {
        if (error.response?.data) {
          console.error(error.response.data);
        }
      } else {
        console.error(error);
      }
      return { success: false };
    }
  }

  async putWithResponse<TResponse>(
    path: string,
    data?: object
  ): Promise<TResponse> {
    try {
      const response = await this.client.put(path, data);
      return response.data;
    } catch (error) {
      if (Axios.isAxiosError(error)) {
        if (error.response?.data) {
          console.error(error.response.data);
        }
      } else {
        console.error(error);
      }
      return {} as TResponse;
    }
  }
}
